import { Button, Input, Label, Modal } from '@iwoca/orion';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import {
  usePatchIntroducerPermissions,
  usePatchIntroducerStatus,
} from 'lendingApi/lendingApiHooks';
import { FormProvider, useForm } from 'react-hook-form';
import { showSuccessToast } from '../../UserManagementToast';
import {
  diffPermissions,
  TEditUserFormValues,
} from '../EditUserModal/EditUserModal.helpers';
import { UserPermissionsFields } from '../UserPermissionsFields/UserPermissionsFields';
import { TIntroducerListItem } from '../UsersTable.types';
import styles from './ReactivateUserModal.module.css';

type TProps = {
  user: TIntroducerListItem;
  isOpen: boolean;
  onClose: () => void;
};

export const ReactivateUserModal = ({ user, isOpen, onClose }: TProps) => {
  const { introducerCompany } = useIntroducerData();
  const patchPermissions = usePatchIntroducerPermissions();
  const patchStatus = usePatchIntroducerStatus();

  const defaultPermissions =
    introducerCompany?.default_permissions ?? user.permissions;

  const methods = useForm<TEditUserFormValues>({
    values: { permissions: defaultPermissions },
  });

  const isPending = patchPermissions.isPending || patchStatus.isPending;
  const isError = patchPermissions.isError || patchStatus.isError;

  const onSubmit = async (values: TEditUserFormValues) => {
    patchPermissions.reset();
    patchStatus.reset();

    const changedPermissions = diffPermissions(
      user.permissions,
      values.permissions,
    );

    try {
      if (Object.keys(changedPermissions).length > 0) {
        await patchPermissions.mutateAsync({
          introducerId: user.id,
          body: { data: changedPermissions },
        });
      }
      await patchStatus.mutateAsync({
        introducerId: user.id,
        body: { data: { is_active: true } },
      });
      showSuccessToast('User has been reactivated');
      onClose();
    } catch {
      // isError surfaces the inline message; activation is skipped if the
      // permissions patch fails because status runs only after it resolves.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Reactivate user - ${user.first_name} ${user.last_name}`}
      size="large"
    >
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={styles.identityGrid}>
            <div className={styles.field}>
              <Label htmlFor="reactivate-first-name">First name</Label>
              <Input
                id="reactivate-first-name"
                value={user.first_name}
                disabled
                readOnly
              />
            </div>
            <div className={styles.field}>
              <Label htmlFor="reactivate-last-name">Last name</Label>
              <Input
                id="reactivate-last-name"
                value={user.last_name}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className={styles.field}>
            <Label htmlFor="reactivate-email">Email</Label>
            <Input
              id="reactivate-email"
              value={user.email ?? ''}
              disabled
              readOnly
            />
          </div>
          <div className={styles.field}>
            <Label htmlFor="reactivate-phone">Phone</Label>
            <Input
              id="reactivate-phone"
              value={user.phone_number ?? ''}
              disabled
              readOnly
            />
          </div>
          <UserPermissionsFields />
          {isError && (
            <p className={styles.error}>
              Couldn’t reactivate user. Please try again.
            </p>
          )}
          <div className={styles.footer}>
            <Button
              variant="tertiary"
              type="button"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={isPending}>
              Reactivate user
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
